// Copy and SEO data for the SSL, DNS and domain tool pages (openspec change 117).
// One entry per page. The exact search phrase (see proposal.md) is in the slug, the title,
// the H1, the first paragraph and at least two H2 headings. The FAQ here is the visible FAQ
// and also the FAQPage JSON-LD, so the two can never differ.

export type ToolId =
  | 'ssl-checker'
  | 'dns-lookup'
  | 'mx-lookup'
  | 'txt-record-lookup'
  | 'dns-propagation-checker'
  | 'domain-expiry-checker'
  | 'whois-lookup'
  | 'domain-age-checker'

export type FaqItem = { question: string; answer: string }

export type ContentSection = {
  h2: string
  paragraphs?: string[]
  items?: Array<{ term: string; text: string }>
}

export type CtaNoun = 'SSL certificate' | 'DNS record' | 'domain'

export type ToolContent = {
  /** Tool name sent with the funnel events. */
  tool: string
  /** Path without the origin, for example `/tools/ssl-checker`. */
  path: string
  /** Short name for breadcrumbs and link text. */
  name: string
  h1: string
  title: string
  description: string
  /** Slug of the OG image in `public/og/` (without `.png`). */
  ogSlug: string
  /** First paragraph. It carries the exact primary phrase. */
  intro: string
  /** One honest line under the form: where the data comes from. */
  sourceNote: string
  sections: ContentSection[]
  faq: FaqItem[]
  related: ToolId[]
  cta: { noun: CtaNoun; featureHref?: string; featureLabel?: string }
}

export const BASE_URL = 'https://uptrack.app'

export const TOOL_BLURBS: Record<ToolId, { name: string; blurb: string }> = {
  'ssl-checker': { name: 'SSL Checker', blurb: 'Check an SSL certificate: validity, expiry date, issuer, names covered and chain.' },
  'dns-lookup': { name: 'DNS Lookup', blurb: 'Look up A, AAAA, CNAME, MX, NS, TXT, SOA, CAA and PTR records on three public resolvers.' },
  'mx-lookup': { name: 'MX Lookup', blurb: 'Find the mail servers of a domain and their priority.' },
  'txt-record-lookup': { name: 'TXT Record Lookup', blurb: 'Read the TXT records of a domain, including SPF, DKIM and DMARC.' },
  'dns-propagation-checker': { name: 'DNS Propagation Checker', blurb: 'Compare one DNS record across Cloudflare, Google and Quad9 and spot differences.' },
  'domain-expiry-checker': { name: 'Domain Expiry Checker', blurb: 'See when a domain expires and how many days are left.' },
  'whois-lookup': { name: 'WHOIS Lookup', blurb: 'Registrar, dates, status and name servers of a domain, from RDAP.' },
  'domain-age-checker': { name: 'Domain Age Checker', blurb: 'How old a domain is, from its registration date.' },
}

const DNS_SOURCE =
  'Answers come from three public DNS resolvers (Cloudflare, Google and Quad9), not from three countries.'
const DOMAIN_SOURCE =
  'Data comes from RDAP, the registry’s own lookup service. Some top-level domains publish no RDAP data.'

const SSL_FEATURE = { featureHref: '/guides/ssl-monitoring', featureLabel: 'How to get warned before a certificate expires' }
const DNS_FEATURE = { featureHref: '/features/dns-monitoring', featureLabel: 'How DNS monitoring works' }

export const TOOLS: Record<ToolId, ToolContent> = {
  'ssl-checker': {
    tool: 'ssl-checker',
    path: '/tools/ssl-checker',
    name: 'SSL Checker',
    h1: 'SSL Checker',
    title: 'SSL Checker: Certificate Expiry and Chain | Uptrack',
    description:
      'Free SSL checker: see if an SSL certificate is valid, when it expires, who issued it and which names it covers. Enter a hostname to check.',
    ogSlug: 'tools-ssl-checker',
    intro:
      'This free SSL checker connects to a host, reads its SSL certificate and shows whether it is valid, when it expires and who issued it. Enter a hostname to see the expiry date, the certificate chain and the names the certificate covers.',
    sourceNote:
      'Checked from Germany with a TLS handshake. The checker sends no web request and no other data to the host.',
    sections: [
      {
        h2: 'How this SSL checker works',
        paragraphs: [
          'The checker opens a TLS connection to the host and port you enter, from our servers in Germany. It reads the certificate that the server presents, validates it against the public root certificates, and reports the dates, issuer, names and chain.',
          'The check runs from one location. If a CDN or a load balancer serves different certificates by region, a visitor elsewhere can see a different result.',
        ],
      },
      {
        h2: 'How to check SSL certificate expiration',
        paragraphs: [
          'Enter the hostname and read the days remaining and the valid-until date. A result under 30 days turns into a warning. Browsers reject a certificate after its end date, so renew it before that day.',
          'Many certificate authorities support automatic renewal through ACME clients. If you use it, check that the renewal job runs and that the server loads the new certificate.',
        ],
      },
      {
        h2: 'What an SSL checker result means',
        items: [
          { term: 'Valid and trusted', text: 'The certificate is inside its validity dates and passed validation against the public root certificates. Check that your hostname is in the names covered.' },
          { term: 'Expires soon or expired', text: 'The valid-until date is close or past. Visitors see a browser warning once it has passed.' },
          { term: 'Not trusted', text: 'The certificate failed validation. Common causes are a self-signed certificate, a missing intermediate certificate, a certificate issued for another hostname, or an expired certificate. The result shows the reason when it is known.' },
          { term: 'Names covered', text: 'The hostnames the certificate is valid for (Subject Alternative Names). A wildcard such as *.example.com covers one level of subdomains, not example.com itself.' },
          { term: 'Certificate chain', text: 'The server certificate, then the intermediate certificates that link it to a trusted root. Each row shows the subject, the issuer and the validity dates.' },
          { term: 'Protocol and cipher', text: 'The TLS version and cipher suite negotiated in this handshake. TLS 1.0 and 1.1 are deprecated.' },
        ],
      },
      {
        h2: 'SSL certificate checker or SSL monitor: which one do you need',
        paragraphs: [
          'A one-time check shows the state of a certificate today. A certificate fails on a date, and visitors notice before you do. A monitor checks the certificate on a schedule and alerts you before it expires.',
        ],
      },
    ],
    faq: [
      {
        question: 'How do I check SSL certificate expiration date?',
        answer:
          'Enter the hostname above and press Check. The result shows the valid-until date and the days remaining. You can also click the padlock in your browser and open the certificate details. This tool shows the same data without opening the site, and it also works for mail servers on ports 465, 993 and 995.',
      },
      {
        question: 'Why is my certificate chain incomplete or not trusted?',
        answer:
          'A server should send its own certificate and the intermediate certificates that link it to a trusted root. If the server leaves out an intermediate certificate, some clients cannot build the path to a root and report the certificate as not trusted. Install the full chain on the server (often called the fullchain or bundle file). A self-signed certificate, an expired certificate or a certificate for another hostname also fails validation.',
      },
      {
        question: 'How long are SSL certificates valid?',
        answer:
          'Browsers and certificate authorities limit how long a certificate may be valid, and the limit has been shrinking. Read the valid-from and valid-until dates of your own certificate and plan renewal around the end date. Automatic renewal removes the manual step.',
      },
      {
        question: 'Which ports can I check?',
        answer:
          'Port 443 is the default. Under Advanced options you can choose 465, 993, 995 or 8443. These ports use TLS from the first byte. Ports that start in plain text and upgrade later (STARTTLS), such as 25 and 587, are not supported.',
      },
      {
        question: 'Is the result the same from every location?',
        answer:
          'This tool checks from one location, Germany. Most sites serve the same certificate everywhere. A CDN or a load balancer can serve a different one by region, so test from another network if you suspect that.',
      },
      {
        question: 'Is this SSL checker free?',
        answer:
          'The tool is free and needs no signup. It limits how fast you can run checks, so wait a few seconds between checks. Continuous monitoring with alerts is a paid Uptrack plan.',
      },
    ],
    related: ['domain-expiry-checker', 'dns-lookup', 'whois-lookup'],
    cta: { noun: 'SSL certificate', ...SSL_FEATURE },
  },

  'dns-lookup': {
    tool: 'dns-lookup',
    path: '/tools/dns-lookup',
    name: 'DNS Lookup',
    h1: 'DNS Lookup',
    title: 'DNS Lookup: Check DNS Records Online | Uptrack',
    description:
      'Free DNS lookup: check DNS records (A, AAAA, MX, NS, TXT, CNAME, SOA, CAA, PTR) on Cloudflare, Google and Quad9 and compare their answers.',
    ogSlug: 'tools-dns-lookup',
    intro:
      'Use this DNS lookup to check the DNS records of any domain. Pick a record type, such as A, MX, TXT or NS, and see what three public resolvers (Cloudflare, Google and Quad9) return, with the TTL of each record.',
    sourceNote: DNS_SOURCE,
    sections: [
      {
        h2: 'How this DNS lookup works',
        paragraphs: [
          'The tool sends the same query to Cloudflare (1.1.1.1), Google (8.8.8.8) and Quad9 (9.9.9.9) and shows each answer next to the others. These are recursive resolvers, not the authoritative name servers of the domain, so an answer can come from a cache.',
          'The TTL is the time in seconds that a resolver may keep the answer. A cached answer shows the time left, so the TTL can differ between resolvers.',
        ],
      },
      {
        h2: 'Check DNS records: the record types',
        items: [
          { term: 'A and AAAA', text: 'The IPv4 and IPv6 addresses of a name.' },
          { term: 'CNAME', text: 'An alias that points a name at another name.' },
          { term: 'MX', text: 'The mail servers of a domain, with a priority. A lower number is tried first.' },
          { term: 'NS', text: 'The name servers that are authoritative for the domain.' },
          { term: 'TXT', text: 'Free text, used for SPF, DKIM, DMARC and domain verification.' },
          { term: 'SOA', text: 'Start of authority: the primary name server, a contact, a serial number and timers.' },
          { term: 'CAA', text: 'The certificate authorities that may issue certificates for the domain.' },
          { term: 'PTR', text: 'Reverse lookup: the name that belongs to an IP address. Enter an IP address instead of a name.' },
        ],
      },
      {
        h2: 'Read the DNS lookup result: NOERROR, NXDOMAIN and empty answers',
        paragraphs: [
          'NXDOMAIN means the name does not exist. NOERROR with no records means the name exists but has no record of the type you asked for. Both are valid answers, not errors of the tool.',
          'If one resolver differs from the others, a change may not have reached its cache yet. Use the DNS propagation checker to compare resolvers after a change.',
        ],
      },
      {
        h2: 'Online nslookup: the same query on the web',
        paragraphs: [
          'If you use nslookup or dig on the command line, this DNS checker runs the same kind of query from a web page, without a terminal. It shows the answer of three resolvers at once.',
        ],
      },
    ],
    faq: [
      {
        question: 'How do I check DNS records for a domain?',
        answer:
          'Enter the domain, choose a record type such as A, MX or TXT, and press Look up. The result lists the records that Cloudflare, Google and Quad9 return, with the TTL of each record.',
      },
      {
        question: 'What is the difference between NXDOMAIN and an empty answer?',
        answer:
          'NXDOMAIN means the name does not exist at all. NOERROR with an empty answer means the name exists but has no record of the type you asked for, for example a domain with no MX record.',
      },
      {
        question: 'Why do the resolvers show different TTLs?',
        answer:
          'A resolver that holds the answer in its cache reports the time left, not the original TTL. Two resolvers that cached the answer at different times show different numbers. A different TTL is normal. Different values are not.',
      },
      {
        question: 'Does this tool query the authoritative name servers?',
        answer:
          'No. It queries three public recursive resolvers. Their answers can come from a cache. To see the records at the source, query the name servers listed in the NS records of the domain.',
      },
      {
        question: 'Can I use this as nslookup online?',
        answer:
          'Yes for the common cases. It does the same kind of lookup as nslookup or dig, for nine record types, and shows three resolvers side by side. It does not let you pick a custom resolver or name server.',
      },
      {
        question: 'How do I do a reverse DNS (PTR) lookup?',
        answer:
          'Choose the PTR record type and enter an IP address, for example 8.8.8.8. The tool converts it to the reverse name and returns the hostname that the owner of the address set, if any.',
      },
    ],
    related: ['dns-propagation-checker', 'mx-lookup', 'txt-record-lookup'],
    cta: { noun: 'DNS record', ...DNS_FEATURE },
  },

  'mx-lookup': {
    tool: 'mx-lookup',
    path: '/tools/mx-lookup',
    name: 'MX Lookup',
    h1: 'MX Lookup',
    title: "MX Lookup: Find a Domain's Mail Servers | Uptrack",
    description:
      'Free MX lookup: find the mail servers of a domain and their priority, as seen by three public DNS resolvers. Enter a domain to run an MX record lookup.',
    ogSlug: 'tools-mx-lookup',
    intro:
      'Use this MX lookup to find the mail servers of a domain. It runs an MX record lookup on three public DNS resolvers and lists each mail exchanger with its priority, so you can check where email for the domain is delivered.',
    sourceNote: DNS_SOURCE,
    sections: [
      {
        h2: 'How this MX lookup works',
        paragraphs: [
          'An MX record names a mail server for a domain and gives it a priority number. A sending server tries the lowest number first and moves to the next one if that server does not answer. Records with the same number share the load.',
          'The tool asks Cloudflare, Google and Quad9 for the MX records of the domain. If the three agree, the result says so. If they differ, a recent change may not have reached every cache yet.',
        ],
      },
      {
        h2: 'Email server check: what to look for in an MX lookup',
        items: [
          { term: 'Hostnames, not IP addresses', text: 'An MX record must point to a hostname. That hostname needs its own A or AAAA record, and it should not be a CNAME.' },
          { term: 'The expected provider', text: 'The hostnames should belong to the provider you use for email. A leftover record from an old provider can send mail to the wrong place.' },
          { term: 'Priorities', text: 'A backup server should have a higher number than the main one.' },
        ],
      },
      {
        h2: 'MX lookup shows no records: what it means',
        paragraphs: [
          'If a domain has no MX record, many mail servers fall back to the A or AAAA record of the domain. A domain that does not receive email can publish a null MX record (priority 0 and a single dot) to say so.',
          'Next, check the TXT records of the domain for SPF, DKIM and DMARC. They control who may send email for it.',
        ],
      },
    ],
    faq: [
      {
        question: 'How do I check the MX records of a domain?',
        answer:
          'Enter the domain above and press Look up. The result lists the mail servers with their priority and TTL, for Cloudflare, Google and Quad9.',
      },
      {
        question: 'What does the MX priority number mean?',
        answer:
          'It sets the order in which sending servers try the mail servers. The lowest number is tried first. Servers with the same number share the traffic.',
      },
      {
        question: 'How do I find the mail server for an email address?',
        answer:
          'Take the part after the @ sign, for example example.com in name@example.com, and run an MX lookup on it.',
      },
      {
        question: 'Why does a domain have no MX records?',
        answer:
          'It may not receive email, or the records may be missing by mistake. Without an MX record, many servers try the A or AAAA record of the domain instead. A domain that should not receive email can publish a null MX record.',
      },
      {
        question: 'Why do the three resolvers show different MX records?',
        answer:
          'A resolver keeps an answer until its TTL runs out. After a change, a resolver can still return the old records for a while. Check again after the TTL has passed.',
      },
    ],
    related: ['txt-record-lookup', 'dns-lookup', 'dns-propagation-checker'],
    cta: { noun: 'DNS record', ...DNS_FEATURE },
  },

  'txt-record-lookup': {
    tool: 'txt-record-lookup',
    path: '/tools/txt-record-lookup',
    name: 'TXT Record Lookup',
    h1: 'TXT Record Lookup',
    title: 'TXT Record Lookup: SPF, DKIM, DMARC | Uptrack',
    description:
      'Free TXT record lookup: read the TXT records of a domain, including SPF, DKIM and DMARC records, from three public DNS resolvers.',
    ogSlug: 'tools-txt-record-lookup',
    intro:
      'Use this TXT record lookup to read the TXT records of a domain, including SPF, DKIM and DMARC records. Enter a domain, or a full name such as _dmarc.example.com, and compare the answer of three public DNS resolvers.',
    sourceNote: DNS_SOURCE,
    sections: [
      {
        h2: 'TXT record lookup for SPF, DKIM and DMARC',
        items: [
          { term: 'SPF', text: 'Lists the servers that may send email for the domain. It is a TXT record on the domain itself and starts with v=spf1.' },
          { term: 'DKIM', text: 'Holds the public key that receivers use to check the signature on a message. It sits at selector._domainkey.example.com. The selector is chosen by the sender, so you need it from the DKIM-Signature header (the s= tag) of a message.' },
          { term: 'DMARC', text: 'Tells receivers what to do with mail that fails SPF and DKIM. It sits at _dmarc.example.com and starts with v=DMARC1.' },
        ],
      },
      {
        h2: 'How to use this TXT record lookup',
        paragraphs: [
          'Enter the domain to list its TXT records. Enter a longer name to look at one record, for example _dmarc.example.com for DMARC. The quick links below the form fill in these names for you. The DKIM link uses default as an example selector, so edit it to match your sender.',
          'The tool asks Cloudflare, Google and Quad9. If they return different values, a recent change may not have reached every cache.',
        ],
      },
      {
        h2: 'Other TXT records you may see',
        paragraphs: [
          'Many services ask you to prove that you control a domain by adding a TXT record with a token they give you. A domain can have many TXT records, so a long list is normal. Remove tokens that you no longer use.',
        ],
      },
    ],
    faq: [
      {
        question: 'How do I check the SPF record of a domain?',
        answer:
          'Run a TXT record lookup on the domain and find the value that starts with v=spf1. A domain should have only one SPF record. If you see two, receivers can treat SPF as an error.',
      },
      {
        question: 'How do I find the DKIM record?',
        answer:
          'Look up selector._domainkey.example.com as a TXT record. Replace selector with the one your email service uses. You can find it in the s= tag of the DKIM-Signature header of a message that the service sent.',
      },
      {
        question: 'How do I check a DMARC record?',
        answer:
          'Run a TXT record lookup on _dmarc.example.com, with your own domain in place of example.com. A valid record starts with v=DMARC1 and has a p= policy tag.',
      },
      {
        question: 'Can a domain have more than one SPF record?',
        answer:
          'No. A name must have a single record that starts with v=spf1. To allow more senders, merge them into that one record.',
      },
      {
        question: 'Why is my new TXT record not showing?',
        answer:
          'Check that you added it to the zone of the right name servers. Then wait for the TTL of the old answer to pass, because resolvers can keep a cached answer, including a cached no-record answer, until then.',
      },
    ],
    related: ['mx-lookup', 'dns-lookup', 'dns-propagation-checker'],
    cta: { noun: 'DNS record', ...DNS_FEATURE },
  },

  'dns-propagation-checker': {
    tool: 'dns-propagation-checker',
    path: '/tools/dns-propagation-checker',
    name: 'DNS Propagation Checker',
    h1: 'DNS Propagation Checker',
    title: 'DNS Propagation Checker: Compare 3 Resolvers | Uptrack',
    description:
      'Free DNS propagation checker: ask Cloudflare, Google and Quad9 for the same record and see if they agree. Differences are highlighted.',
    ogSlug: 'tools-dns-propagation-checker',
    intro:
      'Use this DNS propagation checker to see whether a DNS change has reached public resolvers. It asks Cloudflare, Google and Quad9 for the same record and highlights any difference between their answers.',
    sourceNote:
      'Three public resolvers are a sample, not every location. Resolvers of ISPs and other networks can still hold an older answer.',
    sections: [
      {
        h2: 'How this DNS propagation checker works',
        paragraphs: [
          'The tool sends one query for the name and record type to Cloudflare, Google and Quad9 at the same time. It then groups the answers by value, so you see at a glance which resolvers return the old value and which return the new one.',
        ],
      },
      {
        h2: 'How DNS propagation works',
        paragraphs: [
          'DNS does not push a change to the world. Each resolver keeps an answer in its cache until the TTL of that answer runs out, then asks again. After you change a record, a resolver shows the new value only after its cached copy has expired.',
          'This is why the wait depends on the TTL that the old record had. Lower the TTL some time before a planned change to shorten the wait.',
        ],
      },
      {
        h2: 'How to check DNS propagation',
        paragraphs: [
          'Enter the name and the record type that you changed, then press Check. Each row of the result is one value. The table shows which resolvers return it and with what TTL. A value that one resolver does not return is marked as different.',
          'When all resolvers that answered return the same set of values, the result says they agree. TTL numbers can differ and that does not count as a difference.',
        ],
      },
      {
        h2: 'What a DNS propagation checker with three resolvers can tell you',
        paragraphs: [
          'Cloudflare, Google and Quad9 are large public resolvers. They are not three countries: each one runs many servers worldwide, and you reach one of them. If they all show the new value, most people who use these resolvers see it too.',
          'It does not prove that every user sees the change. Home routers, ISPs and company networks run their own caches, and some keep an old answer longer than its TTL.',
        ],
      },
    ],
    faq: [
      {
        question: 'How long does DNS propagation take?',
        answer:
          'It depends on the TTL of the old record. A resolver can keep the old answer until that TTL runs out. A change to the name servers of a domain can take longer, because the parent zone caches the delegation with its own TTL. Lowering the TTL before a change shortens the wait.',
      },
      {
        question: 'Why do the resolvers show different answers?',
        answer:
          'They cached the record at different times, so some of them still hold the old value. Check again after the TTL has passed. If the difference stays, check the records at the authoritative name servers.',
      },
      {
        question: 'Does a matching result prove that everyone sees the change?',
        answer:
          'No. It shows that three large public resolvers agree. Other resolvers, such as your ISP, can still return an older answer.',
      },
      {
        question: 'Can I check DNS propagation from different countries?',
        answer:
          'Not with this tool. It compares three public resolvers, not countries. The result is labeled by resolver for that reason.',
      },
      {
        question: 'Which record type should I check?',
        answer:
          'Check the type you changed: A or AAAA for a web server address, CNAME for an alias, MX for mail, TXT for SPF or DKIM, NS for a move to other name servers.',
      },
    ],
    related: ['dns-lookup', 'txt-record-lookup', 'mx-lookup'],
    cta: { noun: 'DNS record', ...DNS_FEATURE },
  },

  'domain-expiry-checker': {
    tool: 'domain-expiry-checker',
    path: '/tools/domain-expiry-checker',
    name: 'Domain Expiry Checker',
    h1: 'Domain Expiry Checker',
    title: 'Domain Expiry Checker: Expiration Date | Uptrack',
    description:
      "Free domain expiry checker: find the domain expiration date, the days left and the registrar. Data comes from the registry's RDAP service.",
    ogSlug: 'tools-domain-expiry-checker',
    intro:
      'Use this domain expiry checker to find out when a domain expires. Enter a domain to see its expiration date, the days remaining, the registrar and the registry status, read from the registry’s RDAP service.',
    sourceNote: DOMAIN_SOURCE,
    sections: [
      {
        h2: 'How the domain expiry checker works',
        paragraphs: [
          'The tool asks the registry’s RDAP service for the record of the domain and reads the expiration date from it. RDAP is the newer, structured replacement for WHOIS. WHOIS is the older plain-text protocol. We read RDAP, so a top-level domain that publishes no RDAP data gives no result.',
        ],
      },
      {
        h2: 'When does my domain expire? Reading the result',
        paragraphs: [
          'The result shows the expiration date in UTC and the days remaining. Under 30 days it turns into a warning. If the date has already passed, the domain may be in a grace or redemption period at the registrar, or it may already have been released, so check with your registrar at once.',
          'The registry date is the date the registration ends. Your registrar may show a renewal date or a billing date that is not the same, so check both.',
        ],
      },
      {
        h2: 'Domain expiry checker result: what to do when the date is close',
        paragraphs: [
          'Renew the domain at your registrar. Turn on auto-renew and keep a valid payment method on file. Some registries allow a grace period after expiry, but the length and the fees differ, and the site and email may stop working in the meantime, so do not plan on it.',
        ],
      },
    ],
    faq: [
      {
        question: 'When does my domain expire?',
        answer:
          'Enter the domain above and press Check. The expiration date and the days remaining are at the top of the result. Your registrar account also shows the date, and it is the place to renew.',
      },
      {
        question: 'What happens when a domain expires?',
        answer:
          'The rules depend on the registry and the registrar. Websites and email on the domain can stop working, and the domain can enter a grace period or a redemption period before it is released. Renew before the expiration date.',
      },
      {
        question: 'Why does the tool show no data for my domain?',
        answer:
          'The registry returned no RDAP record for the name. Some country-code top-level domains, such as .io, publish no RDAP data, and a name that is not registered has no record. Enter the registered domain, such as example.com, not a subdomain. This result does not prove that the name is available. Ask a registrar to check.',
      },
      {
        question: 'Is RDAP the same as WHOIS?',
        answer:
          'No. Both publish registration data. WHOIS is the old text protocol. RDAP is its structured successor and runs over HTTPS. This tool uses RDAP.',
      },
      {
        question: 'Can I get an alert before my domain expires?',
        answer:
          'A one-time check does not alert you. Uptrack monitoring can check a domain on a schedule and send an alert before the date. It is a paid plan, from $3/mo.',
      },
    ],
    related: ['whois-lookup', 'domain-age-checker', 'ssl-checker'],
    cta: { noun: 'domain' },
  },

  'whois-lookup': {
    tool: 'whois-lookup',
    path: '/tools/whois-lookup',
    name: 'WHOIS Lookup',
    h1: 'WHOIS Lookup',
    title: 'WHOIS Lookup: Registrar, Dates, Name Servers | Uptrack',
    description:
      "Free WHOIS lookup: see a domain's registrar, creation, update and expiry dates, status and name servers. Data comes from RDAP, the WHOIS successor.",
    ogSlug: 'tools-whois-lookup',
    intro:
      'Use this WHOIS lookup to see the registration data of a domain: the registrar, the registration date, the update and expiry dates, the status codes and the name servers. The data comes from RDAP, the modern replacement for WHOIS.',
    sourceNote: DOMAIN_SOURCE,
    sections: [
      {
        h2: 'How this WHOIS lookup works',
        paragraphs: [
          'WHOIS is the old plain-text protocol for domain registration data. RDAP is its structured successor, run by registries over HTTPS. This tool reads RDAP, so a top-level domain that publishes no RDAP data gives no result.',
        ],
      },
      {
        h2: 'WHOIS lookup fields: what each one means',
        items: [
          { term: 'Registrar', text: 'The company the domain is registered through.' },
          { term: 'Registered (created)', text: 'The date of the current registration. It can be later than the first use of the name if the domain was dropped and registered again.' },
          { term: 'Updated', text: 'The last change to the registration record.' },
          { term: 'Expires', text: 'The date the registration ends unless it is renewed.' },
          { term: 'Status', text: 'Codes set by the registry or registrar. For example, client transfer prohibited means the registrar has locked the domain against transfer.' },
          { term: 'Name servers', text: 'The servers that hold the DNS records of the domain.' },
        ],
      },
      {
        h2: 'Why a WHOIS lookup does not show the owner',
        paragraphs: [
          'Registrant names and contact details are usually redacted by registries or hidden by privacy services, so this tool does not show them. To contact a domain owner, use the contact form or the abuse contact of the registrar.',
        ],
      },
      {
        h2: 'Domain registration date',
        paragraphs: [
          'The registered date is in the result under Registered. The domain age checker turns it into years and days.',
        ],
      },
    ],
    faq: [
      {
        question: 'What is a WHOIS lookup?',
        answer:
          'It is a query for the registration record of a domain: who the registrar is, when the domain was registered and when it expires, its status and its name servers.',
      },
      {
        question: 'Is WHOIS the same as RDAP?',
        answer:
          'No. WHOIS is the old text protocol. RDAP is the structured successor and runs over HTTPS. This tool uses RDAP.',
      },
      {
        question: 'How do I find who owns a domain?',
        answer:
          'Usually you cannot. Registries redact personal data, and many owners use a privacy service. The result shows the registrar, and you can contact the owner through the registrar.',
      },
      {
        question: 'How do I check a domain registration date?',
        answer:
          'Enter the domain and read the Registered date in the result. For the age in years and days, use the domain age checker.',
      },
      {
        question: 'Why does the lookup show no data for a domain?',
        answer:
          'The registry returned no RDAP record. Some country-code top-level domains, such as .io, publish no RDAP data, and a name that is not registered has no record. This does not prove that the name is available. Ask a registrar to check.',
      },
    ],
    related: ['domain-expiry-checker', 'domain-age-checker', 'dns-lookup'],
    cta: { noun: 'domain' },
  },

  'domain-age-checker': {
    tool: 'domain-age-checker',
    path: '/tools/domain-age-checker',
    name: 'Domain Age Checker',
    h1: 'Domain Age Checker',
    title: 'Domain Age Checker: Registration Date | Uptrack',
    description:
      "Free domain age checker: find out how old a domain is and when it was registered, in years and days. Data comes from the registry's RDAP service.",
    ogSlug: 'tools-domain-age-checker',
    intro:
      'Use this domain age checker to see how old a domain is. Enter a domain to get its registration date and its age in years and days, read from the registry’s RDAP service.',
    sourceNote: DOMAIN_SOURCE,
    sections: [
      {
        h2: 'How the domain age checker works',
        paragraphs: [
          'The tool reads the creation date from the RDAP record of the domain and counts the time from that date to today. The age is shown in years and days, and the registration date is shown next to it.',
        ],
      },
      {
        h2: 'Domain registration date: what it tells you and what it does not',
        paragraphs: [
          'The date is the start of the current registration. If a domain expired, was released and was registered again, the date can be recent even though the name is old. A domain can also be registered long before a site goes live on it.',
          'So the age is one fact about the name. It does not show how long a website has run on it or whether the site is trustworthy.',
        ],
      },
      {
        h2: 'Domain age checker: use it before you buy a domain',
        paragraphs: [
          'Look at the registration date, the expiry date and the status together. Then check the DNS records and the SSL certificate to see if the domain is in use. The links below open those tools.',
        ],
      },
    ],
    faq: [
      {
        question: 'How do I check the age of a domain?',
        answer:
          'Enter the domain above and press Check. The age in years and days is at the top of the result, with the registration date.',
      },
      {
        question: 'Where does the registration date come from?',
        answer:
          'It comes from the RDAP record that the registry publishes for the domain. This tool reads that record live and stores nothing.',
      },
      {
        question: 'Why is the registration date newer than the website?',
        answer:
          'The date marks the current registration. If the domain lapsed and someone registered it again, the date starts over. A site may also have used the name under a different registration earlier.',
      },
      {
        question: 'Why does the tool show no age for my domain?',
        answer:
          'The registry returned no RDAP record, or the record has no creation date. Some country-code top-level domains, such as .io, publish no RDAP data. Enter the registered domain, not a subdomain.',
      },
    ],
    related: ['whois-lookup', 'domain-expiry-checker', 'ssl-checker'],
    cta: { noun: 'domain' },
  },
}

// ── Per-domain pages ─────────────────────────────────────────────────────────

export type DomainToolId = 'ssl-checker' | 'dns-lookup' | 'domain-expiry-checker'

const SAFE_DOMAIN = /^[a-z0-9_]([a-z0-9_.-]{0,251}[a-z0-9_])?$/

/** The domain in lowercase when it is safe to put in a title, a URL and JSON-LD; otherwise null. */
export function safeDomain(raw: string): string | null {
  const d = raw.trim().toLowerCase()
  return SAFE_DOMAIN.test(d) && d.includes('.') ? d : null
}

function fits(candidates: string[], max: number): string {
  return candidates.find((c) => c.length <= max) ?? candidates[candidates.length - 1]
}

/** Content for `/tools/{tool}/{domain}`. The explainer sections of the parent tool stay on the parent page. */
export function domainPageContent(tool: DomainToolId, rawDomain: string): ToolContent {
  const parent = TOOLS[tool]
  const d = safeDomain(rawDomain) ?? 'this domain'
  const path = `${parent.path}/${safeDomain(rawDomain) ?? 'invalid'}`
  const clip = (s: string) => (s.length <= 155 ? s : `${s.slice(0, 154).replace(/\s+\S*$/, '')}…`)

  if (tool === 'ssl-checker') {
    return {
      ...parent,
      path,
      h1: `SSL certificate for ${d}`,
      title: fits([`${d} SSL Certificate: Expiry Check | Uptrack`, `${d} SSL Certificate | Uptrack`, `SSL Certificate Check | Uptrack`], 60),
      description: clip(`SSL certificate check for ${d}: is it valid, when does it expire, who issued it and which names does it cover. Free live SSL checker.`),
      intro: `This SSL checker reads the live SSL certificate of ${d}. It shows whether the certificate is valid and trusted, when it expires, who issued it and which names it covers. The result loads when you open the page.`,
      sections: [
        {
          h2: `How to check the SSL certificate of ${d}`,
          paragraphs: [
            `The checker connects to ${d} on port 443 from Germany, reads the certificate and validates it. To check another host or a mail port, use the form above or the SSL checker.`,
          ],
        },
      ],
      faq: [
        {
          question: `When does the SSL certificate of ${d} expire?`,
          answer: `The result above shows the valid-until date and the days remaining. It is a live check, so it reflects the certificate that ${d} serves now.`,
        },
        {
          question: `Why does the result for ${d} look different from my browser?`,
          answer: `This tool checks from one location, Germany, and validates against public root certificates. A CDN can serve another certificate by region, and a company network can add its own roots.`,
        },
      ],
      related: ['dns-lookup', 'domain-expiry-checker', 'ssl-checker'],
    }
  }

  if (tool === 'dns-lookup') {
    return {
      ...parent,
      path,
      h1: `DNS records for ${d}`,
      title: fits([`DNS Lookup for ${d}: DNS Records | Uptrack`, `${d} DNS Lookup | Uptrack`, `DNS Lookup | Uptrack`], 60),
      description: clip(`DNS lookup for ${d}: check its A, AAAA, MX, NS, TXT and other DNS records on Cloudflare, Google and Quad9. Free live DNS checker.`),
      intro: `This DNS lookup shows the DNS records of ${d}. Choose a record type, such as A, MX, TXT or NS, and compare what Cloudflare, Google and Quad9 return. The A record loads when you open the page.`,
      sections: [
        {
          h2: `How to check the DNS records of ${d}`,
          paragraphs: [
            `The tool sends the query for ${d} to three public resolvers and shows each answer with its TTL. Change the record type in the form to see MX, TXT, NS and others.`,
          ],
        },
      ],
      faq: [
        {
          question: `How do I check the MX or TXT records of ${d}?`,
          answer: `Choose MX or TXT in the record type list above and press Look up. The result lists the records that three public resolvers return for ${d}.`,
        },
        {
          question: `Why can the answer for ${d} differ between resolvers?`,
          answer: `A resolver keeps an answer in its cache until the TTL runs out. After a change, some resolvers can still hold the old value for a while.`,
        },
      ],
      related: ['dns-propagation-checker', 'mx-lookup', 'txt-record-lookup'],
    }
  }

  return {
    ...parent,
    path,
    h1: `${d} domain expiry date`,
    title: fits([`${d} Domain Expiry Date Checker | Uptrack`, `${d} Domain Expiry Date | Uptrack`, `Domain Expiry Checker | Uptrack`], 60),
    description: clip(`Domain expiry checker for ${d}: expiration date, days remaining, registrar and status, read live from the registry's RDAP service.`),
    intro: `This domain expiry checker shows when ${d} expires. It reads the expiration date, the days remaining, the registrar and the status from the registry’s RDAP service. The result loads when you open the page.`,
    sections: [
      {
        h2: `When does ${d} expire?`,
        paragraphs: [
          `The expiration date of ${d} is at the top of the result. To check another name, use the form above or the domain expiry checker.`,
        ],
      },
    ],
    faq: [
      {
        question: `When does ${d} expire?`,
        answer: `The result above shows the expiration date and the days remaining, read live from the RDAP record of the registry.`,
      },
      {
        question: `Why does the result for ${d} show no data?`,
        answer: `The registry returned no RDAP record. Some country-code top-level domains, such as .io, publish no RDAP data. This does not show whether the name is registered or available.`,
      },
    ],
    related: ['whois-lookup', 'domain-age-checker', 'ssl-checker'],
  }
}
